/**
 * FASE 9.5 — Admin Pratiche Fine Arts (Lloyd's).
 *
 * Gemella semplificata di PraticheCyberPage — Fine Arts ha una sola compagnia
 * (lloyds_fine_arts). Endpoint:
 *   GET /api/admin/fine-arts/pratiche
 *   GET /api/admin/fine-arts/pratiche/{qid}
 *   GET /api/admin/fine-arts/pratiche/{qid}/pdf
 *   GET /api/admin/fine-arts/pratiche/{qid}/thumb?page=1..4
 */
import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast, Toaster } from "sonner";
import { Loader2, Download, RefreshCw, Search, FileText, X, CheckCircle2, Palette } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/admin/fine-arts/pratiche`;

async function downloadAdminFile(url, fallbackName) {
  const res = await axios.get(url, { responseType: "blob", withCredentials: true });
  const cd = res.headers["content-disposition"] || "";
  const m = cd.match(/filename="?([^";]+)"?/);
  const name = m ? m[1] : fallbackName;
  const blob = new Blob([res.data], { type: res.headers["content-type"] });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = name;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => URL.revokeObjectURL(link.href), 1000);
}

function FineArtsThumbnail({ qid, page }) {
  const [src, setSrc] = React.useState(null);
  const [err, setErr] = React.useState(false);
  React.useEffect(() => {
    let cancelled = false;
    let objectUrl = null;
    (async () => {
      try {
        const res = await axios.get(`${API}/${qid}/thumb?page=${page}`, {
          responseType: "blob", withCredentials: true });
        if (cancelled) return;
        objectUrl = URL.createObjectURL(res.data);
        setSrc(objectUrl);
      } catch {
        if (!cancelled) setErr(true);
      }
    })();
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [qid, page]);
  if (err) {
    return (
      <div data-testid={`fine-arts-thumb-err-${page}-${qid}`}
            className="w-[75px] h-[100px] border border-[#D9C49A]/40 rounded-sm bg-slate-50 flex items-center justify-center text-[10px] text-slate-400">
        no preview
      </div>
    );
  }
  if (!src) {
    return (
      <div className="w-[75px] h-[100px] border border-[#D9C49A]/40 rounded-sm bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-4 h-4 text-slate-400 animate-spin" />
      </div>
    );
  }
  return (
    <img src={src} alt={`Fine Arts pagina ${page}`}
          data-testid={`fine-arts-thumb-p${page}-${qid}`}
          className="w-[75px] h-auto border border-[#D9C49A]/60 rounded-sm shadow-sm" />
  );
}

function PraticaDetailModal({ qid, onClose }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/${qid}`, { withCredentials: true });
        setDetail(data);
      } catch (e) {
        toast.error("Errore caricamento dettaglio");
      } finally { setLoading(false); }
    })();
  }, [qid]);
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          data-testid="fine-arts-detail-modal">
      <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="p-5 border-b border-[#E5E2DB] flex items-center justify-between">
          <h3 className="font-heading text-lg text-[#0F1F14] flex items-center gap-2">
            <Palette className="w-5 h-5 text-[#163321]" />
            Dettaglio pratica Fine Arts
          </h3>
          <button onClick={onClose} data-testid="fine-arts-detail-close"
                  className="p-2 rounded hover:bg-slate-100">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-5 overflow-y-auto">
          {loading && <Loader2 className="w-6 h-6 animate-spin" />}
          {detail && (
            <>
              <div className="grid grid-cols-2 gap-3 text-sm mb-4">
                <div><span className="text-slate-500">Cliente:</span> <strong>{detail.client_name}</strong></div>
                <div><span className="text-slate-500">Email:</span> {detail.client_email}</div>
                <div><span className="text-slate-500">Tel:</span> {detail.client_telefono || "—"}</div>
                <div><span className="text-slate-500">Firmato:</span> {detail.signature_meta ? "Sì" : "No"}</div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
                {[1,2,3,4].map((p) => (
                  <div key={p} className="text-center">
                    <FineArtsThumbnail qid={qid} page={p} />
                    <p className="text-xs text-slate-500 mt-1">Pagina {p}</p>
                  </div>
                ))}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => downloadAdminFile(`${API}/${qid}/pdf`, `fine_arts_${qid.slice(0,8)}.pdf`)}
                  data-testid="fine-arts-detail-download-pdf"
                  className="px-4 py-2 bg-[#163321] text-white rounded-lg text-sm inline-flex items-center gap-2">
                  <Download className="w-4 h-4" /> Scarica PDF pixel-perfect
                </button>
              </div>
              <details className="mt-4">
                <summary className="cursor-pointer text-sm text-[#4A5E51]">JSON payload completo</summary>
                <pre className="mt-2 p-3 bg-slate-50 rounded text-[10px] overflow-x-auto">
                  {JSON.stringify(detail.form_data || {}, null, 2)}
                </pre>
              </details>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default function PraticheFineArtsPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(API, { withCredentials: true });
      setItems(data.items || []);
    } catch (e) {
      toast.error("Errore caricamento pratiche Fine Arts");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = items.filter((it) => {
    const q = search.toLowerCase();
    if (!q) return true;
    return (it.client_name || "").toLowerCase().includes(q)
      || (it.client_email || "").toLowerCase().includes(q)
      || (it.id || "").toLowerCase().includes(q);
  });

  return (
    <div className="min-h-screen bg-[#FCFCFA] pt-6 pb-16"
          data-testid="pratiche-fine-arts-page">
      <Toaster position="top-right" />
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-heading text-3xl text-[#0F1F14] flex items-center gap-3"
                data-testid="fine-arts-page-title">
              <Palette className="w-7 h-7 text-[#163321]" />
              Pratiche Fine Arts
            </h1>
            <p className="text-sm text-[#4A5E51]">
              Proposte Lloyd&apos;s Fine Arts (Chiodo a Chiodo · Personal Collections)
            </p>
          </div>
          <button onClick={load} data-testid="fine-arts-reload-btn"
                  className="px-4 py-2 border border-[#163321]/30 text-[#163321] rounded-lg text-sm inline-flex items-center gap-2">
            <RefreshCw className="w-4 h-4" /> Ricarica
          </button>
        </div>

        <div className="mb-4 relative">
          <Search className="w-4 h-4 absolute left-3 top-3 text-slate-400" />
          <input type="text" value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Cerca per nome, email, id…"
                  data-testid="fine-arts-search-input"
                  className="w-full pl-9 pr-4 py-2 border border-[#E5E2DB] rounded-lg text-sm" />
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-8 h-8 animate-spin text-[#163321]" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="bg-white border border-[#E5E2DB] rounded-2xl p-16 text-center"
                data-testid="fine-arts-empty-state">
            <Palette className="w-10 h-10 text-[#D9C49A] mx-auto mb-3" />
            <p className="text-[#4A5E51]">Nessuna pratica Fine Arts trovata.</p>
          </div>
        ) : (
          <div className="bg-white border border-[#E5E2DB] rounded-2xl overflow-hidden">
            <table className="w-full text-sm" data-testid="fine-arts-pratiche-table">
              <thead className="bg-[#F5F4F0] text-[#0F1F14] text-left">
                <tr>
                  <th className="px-4 py-3 font-medium">Anteprima</th>
                  <th className="px-4 py-3 font-medium">Cliente</th>
                  <th className="px-4 py-3 font-medium">Email</th>
                  <th className="px-4 py-3 font-medium">Data</th>
                  <th className="px-4 py-3 font-medium">Firma</th>
                  <th className="px-4 py-3 font-medium">Azioni</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((it) => (
                  <tr key={it.id} className="border-t border-[#E5E2DB] hover:bg-[#FDF8E8]/40"
                       data-testid={`fine-arts-row-${it.id.slice(0,8)}`}>
                    <td className="px-4 py-3">
                      <FineArtsThumbnail qid={it.id} page={1} />
                    </td>
                    <td className="px-4 py-3">
                      <strong>{it.client_name || "—"}</strong>
                      <div className="text-xs text-slate-500">#{it.id.slice(0,8)}</div>
                    </td>
                    <td className="px-4 py-3 text-slate-600">{it.client_email || "—"}</td>
                    <td className="px-4 py-3 text-slate-600">
                      {it.created_at ? new Date(it.created_at).toLocaleDateString("it-IT") : "—"}
                    </td>
                    <td className="px-4 py-3">
                      {it.signed
                        ? <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded-full text-xs">
                            <CheckCircle2 className="w-3 h-3" /> Firmato
                          </span>
                        : <span className="text-xs text-slate-400">no</span>}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-2">
                        <button
                          onClick={() => setSelected(it.id)}
                          data-testid={`fine-arts-view-${it.id.slice(0,8)}`}
                          className="p-2 rounded hover:bg-slate-100 text-[#163321]"
                          title="Dettaglio">
                          <FileText className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => downloadAdminFile(`${API}/${it.id}/pdf`, `fine_arts_${it.id.slice(0,8)}.pdf`)}
                          data-testid={`fine-arts-download-${it.id.slice(0,8)}`}
                          className="p-2 rounded hover:bg-slate-100 text-[#163321]"
                          title="Scarica PDF">
                          <Download className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {selected && (
        <PraticaDetailModal qid={selected} onClose={() => setSelected(null)} />
      )}
    </div>
  );
}
